import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import { toast } from 'react-toastify';
import { useAppContext } from './AppContext';
import { SOCKET_URL, SOCKET_EVENTS } from '../utils/constants';

// Create WebSocket context
const SocketContext = createContext();

// Socket provider component
export function SocketProvider({ children }) {
  const { 
    isAuthenticated, 
    addNotification, 
    updateRealtimeData,
    user 
  } = useAppContext();
  
  const [socket, setSocket] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionError, setConnectionError] = useState(null);
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 5;

  // Initialize socket connection
  useEffect(() => {
    if (!isAuthenticated) {
      // Disconnect socket if user is not authenticated
      if (socket) {
        socket.disconnect();
        setSocket(null);
        setIsConnected(false);
      }
      return;
    }

    // Create socket connection
    const newSocket = io(SOCKET_URL, {
      autoConnect: true,
      reconnection: true,
      reconnectionAttempts: maxReconnectAttempts,
      reconnectionDelay: 1000,
      auth: {
        token: localStorage.getItem('auth_token'),
        userId: user?.id,
      },
    });

    // Connection event handlers
    newSocket.on(SOCKET_EVENTS.CONNECT, () => {
      console.log('Socket connected:', newSocket.id);
      setIsConnected(true);
      setConnectionError(null);
      reconnectAttempts.current = 0;

      // Join user-specific room
      if (user?.id) {
        newSocket.emit('join-user-room', user.id);
      }

      // Show connection success notification
      if (reconnectAttempts.current > 0) {
        toast.success('Real-time connection restored');
      }
    });

    newSocket.on(SOCKET_EVENTS.DISCONNECT, (reason) => {
      console.log('Socket disconnected:', reason);
      setIsConnected(false);

      if (reason === 'io server disconnect') {
        // Server initiated disconnect, try to reconnect
        newSocket.connect();
      }
    });

    newSocket.on('connect_error', (error) => {
      console.error('Socket connection error:', error);
      setConnectionError(error.message);
      reconnectAttempts.current++;

      if (reconnectAttempts.current >= maxReconnectAttempts) {
        toast.error('Unable to establish real-time connection. Some features may not work properly.');
      }
    });

    // Business logic event handlers
    newSocket.on(SOCKET_EVENTS.POST_CREATED, (data) => {
      console.log('New post created:', data);
      
      updateRealtimeData('posts', {
        [data.post.id]: data.post,
      });

      addNotification({
        type: 'success',
        title: 'New Post Created',
        message: `Post "${data.post.title}" was created successfully`,
        data: data.post,
      });
    });

    newSocket.on(SOCKET_EVENTS.POST_UPDATED, (data) => {
      console.log('Post updated:', data);
      
      updateRealtimeData('posts', {
        [data.post.id]: data.post,
      });

      if (data.showNotification) {
        addNotification({
          type: 'info',
          title: 'Post Updated',
          message: `Post "${data.post.title}" was updated`,
          data: data.post,
        });
      }
    });

    newSocket.on(SOCKET_EVENTS.POST_PUBLISHED, (data) => {
      console.log('Post published:', data);
      
      updateRealtimeData('posts', {
        [data.post.id]: { ...data.post, status: 'published' },
      });

      addNotification({
        type: 'success',
        title: 'Post Published',
        message: `Post "${data.post.title}" was published to ${data.officeName}`,
        data: data.post,
      });
    });

    newSocket.on(SOCKET_EVENTS.REVIEW_RECEIVED, (data) => {
      console.log('New review received:', data);
      
      updateRealtimeData('reviews', {
        [data.review.id]: data.review,
      });

      // Show different notification based on sentiment
      const notificationType = data.review.sentiment === 'negative' ? 'warning' : 'info';
      const title = data.review.sentiment === 'negative' ? 'Negative Review Received' : 'New Review Received';

      addNotification({
        type: notificationType,
        title,
        message: `${data.review.rating}-star review from ${data.review.customerName} at ${data.officeName}`,
        data: data.review,
      });

      // Play notification sound for negative reviews
      if (data.review.sentiment === 'negative') {
        playNotificationSound();
      }
    });

    newSocket.on(SOCKET_EVENTS.PHOTO_UPLOADED, (data) => {
      console.log('Photo uploaded:', data);
      
      addNotification({
        type: 'success',
        title: 'Photo Uploaded',
        message: `${data.photos.length} photo(s) uploaded successfully`,
        data: data.photos,
      });
    });

    newSocket.on(SOCKET_EVENTS.ANALYTICS_UPDATED, (data) => {
      console.log('Analytics updated:', data);
      
      updateRealtimeData('analytics', data.analytics);

      // Don't show notification for routine analytics updates
      // Only notify for significant changes
      if (data.significantChange) {
        addNotification({
          type: 'info',
          title: 'Analytics Updated',
          message: data.message || 'New analytics data is available',
          data: data.analytics,
        });
      }
    });

    // Custom event handlers
    newSocket.on('notification', (data) => {
      console.log('Server notification:', data);
      
      addNotification({
        type: data.type || 'info',
        title: data.title || 'Notification',
        message: data.message,
        data: data.payload,
      });
    });

    newSocket.on('system-message', (data) => {
      console.log('System message:', data);
      
      toast(data.message, {
        type: data.type || 'info',
        autoClose: data.autoClose || 5000,
      });
    });

    setSocket(newSocket);

    // Cleanup on unmount
    return () => {
      newSocket.disconnect();
      setSocket(null);
      setIsConnected(false);
    };
  }, [isAuthenticated, user?.id]);

  // Helper function to play notification sound
  const playNotificationSound = () => {
    try {
      const audio = new Audio('/notification.mp3');
      audio.volume = 0.5;
      audio.play().catch(console.error);
    } catch (error) {
      console.error('Error playing notification sound:', error);
    }
  };

  // Socket utility functions
  const emit = (event, data) => {
    if (socket && isConnected) {
      socket.emit(event, data);
    } else {
      console.warn('Socket not connected. Cannot emit event:', event);
    }
  };

  const on = (event, handler) => {
    if (socket) {
      socket.on(event, handler);
    }
  };

  const off = (event, handler) => {
    if (socket) {
      socket.off(event, handler);
    }
  };

  // Join specific rooms
  const joinRoom = (room) => {
    emit('join-room', room);
  };

  const leaveRoom = (room) => {
    emit('leave-room', room);
  };

  // Office-specific functions
  const joinOfficeRooms = (officeIds) => {
    officeIds.forEach(officeId => {
      joinRoom(`office-${officeId}`);
    });
  };

  const leaveOfficeRooms = (officeIds) => {
    officeIds.forEach(officeId => {
      leaveRoom(`office-${officeId}`);
    });
  };

  const value = {
    socket,
    isConnected,
    connectionError,
    emit,
    on,
    off,
    joinRoom,
    leaveRoom,
    joinOfficeRooms,
    leaveOfficeRooms,
  };

  return (
    <SocketContext.Provider value={value}>
      {children}
    </SocketContext.Provider>
  );
}

// Custom hook to use socket context
export function useSocket() {
  const context = useContext(SocketContext);
  
  if (context === undefined) {
    throw new Error('useSocket must be used within a SocketProvider');
  }
  
  return context;
}

export default SocketContext;